/**
 * The demo phone's plumbing (#2824).
 *
 * The demo roster page shows a phone. The phone is an iframe that runs the
 * real member app at `/demo-phone/me`, signed in as the staff member picked
 * in the phone's "Viewing as" menu. The frame holds a read-only
 * `demo-member:` token in memory only (never in storage).
 *
 * The lead page and the frame share one origin, so a `BroadcastChannel`
 * lets the lead page tell the frame to re-read its data after Publish
 * roster, a demo change or a replay.
 */
import { apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import type { DemoSessionStateResponse } from "../api/useDemoSessionState";

/** Where the frame's router is mounted, as a path prefix. */
export const DEMO_PHONE_BASENAME = "/demo-phone";

/** The same-origin channel the lead page uses to wake the frame. */
export const DEMO_PHONE_CHANNEL = "tarii-demo-phone";

/** The query key on the frame's address that names the staff member. */
export const DEMO_PHONE_MEMBER_PARAM = "m";

/** True on the frame's own address: `/demo-phone` and everything under it. */
export function isDemoPhonePath(pathname: string): boolean {
  return pathname === DEMO_PHONE_BASENAME || pathname.startsWith(`${DEMO_PHONE_BASENAME}/`);
}

/** True when this page is the phone frame. Safe outside a browser. */
export function inDemoPhoneFrame(): boolean {
  return typeof window !== "undefined" && isDemoPhonePath(window.location?.pathname ?? "");
}

/**
 * The request the phone offers to send: a sick report, a leave, or the
 * give-up of one seat (`give_up`, with the seat's `station` on `from`).
 */
export interface PhoneSuggestion {
  /** The demo change's id. */
  id: string;
  /** `mc` and `leave` are the wire `request_type`; `give_up` is a swap direction. */
  type: "mc" | "leave" | "give_up";
  from: string;
  to: string;
  label: string;
  /** The station of the seat to give up. Only for `give_up`. */
  station?: string;
}

/** The frame's address for one staff member, with the request it offers, if any. */
export function demoPhoneSrc(membershipId: string, suggestion?: PhoneSuggestion | null): string {
  const base = `${DEMO_PHONE_BASENAME}/me?${DEMO_PHONE_MEMBER_PARAM}=${encodeURIComponent(membershipId)}`;
  if (!suggestion) return base;
  const q = new URLSearchParams({
    suggest: suggestion.id,
    type: suggestion.type,
    from: suggestion.from,
    to: suggestion.to,
    label: suggestion.label,
    // The Calendar opens on the month of the day the request is about (#2824).
    date: suggestion.from,
  });
  if (suggestion.station) q.set("station", suggestion.station);
  return `${base}&${q.toString()}`;
}

/** What the frame's address asks the member app to offer. Null when it asks for nothing valid. */
export function readPhoneSuggestion(search: string): PhoneSuggestion | null {
  const q = new URLSearchParams(search);
  const id = q.get("suggest");
  const type = q.get("type");
  const from = q.get("from");
  const to = q.get("to") ?? from;
  const label = q.get("label");
  const isDay = (v: string | null): v is string => v !== null && /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (
    !id ||
    (type !== "mc" && type !== "leave" && type !== "give_up") ||
    !isDay(from) ||
    !isDay(to) ||
    !label
  ) {
    return null;
  }
  if (type === "give_up") {
    const station = q.get("station");
    return station ? { id, type, from, to, label, station } : null;
  }
  return { id, type, from, to, label };
}

/** The request the active change offers, or null (a setup step, no change, another person). */
export function suggestionFor(
  session: DemoSessionStateResponse | undefined,
  personId: string | null,
): PhoneSuggestion | null {
  if (!session || !personId || !session.variant) return null;
  const change = session.changes.find((c) => c.id === session.variant);
  if (!change || change.kind !== "event" || change.member_id !== personId || !change.start) {
    return null;
  }
  const key = change.type ?? change.id;
  const base = { id: change.id, from: change.start, to: change.end ?? change.start };
  if (key === "swap") {
    // Only the giver offers the give-up; the taker's accept is the server's.
    return change.station
      ? { ...base, type: "give_up", label: change.label, station: change.station }
      : null;
  }
  const type = key === "sick_day" || key === "mc" ? "mc" : key === "leave" ? "leave" : null;
  return type ? { ...base, type, label: change.label } : null;
}

/** Dark outline colours a phone takes, one per person: blues, black and other dark shades. */
export const PHONE_RING_TOKENS = [
  "#1e3a8a",
  "#111827",
  "#1d4ed8",
  "#334155",
  "#312e81",
  "#0f3d4c",
] as const;

/** The colour one person's phone outline uses. The same id always gives the same one. */
export function phoneRingToken(membershipId: string): (typeof PHONE_RING_TOKENS)[number] {
  let h = 5381;
  for (let i = 0; i < membershipId.length; i++) h = ((h * 33) ^ membershipId.charCodeAt(i)) >>> 0;
  return PHONE_RING_TOKENS[h % PHONE_RING_TOKENS.length] as (typeof PHONE_RING_TOKENS)[number];
}

/** Guide to phone: show this person. */
export const DEMO_PHONE_PERSON_EVENT = "tarii:demo-phone-person";
/** Phone to guide: the member did the step. */
export const DEMO_GUIDE_ACTION_EVENT = "tarii:demo-guide-action";
/** The frame's message to the lead page after a request goes in. */
export const DEMO_PHONE_SENT_MESSAGE = "tarii-demo-phone-request-sent";

let pendingPerson: string | null = null;

/** While a scenario plays the phone stays on one person (null id: whoever shows now). */
export interface DemoPhoneScenarioLock {
  memberId: string | null;
}
let scenarioLock: DemoPhoneScenarioLock | null = null;
const scenarioLockListeners = new Set<() => void>();

/** Guide: lock the phone to one person, or null to give the picker back. */
export function setDemoPhoneScenarioLock(next: DemoPhoneScenarioLock | null): void {
  if (scenarioLock?.memberId === next?.memberId && Boolean(scenarioLock) === Boolean(next)) return;
  scenarioLock = next;
  for (const listener of Array.from(scenarioLockListeners)) listener();
}

export function getDemoPhoneScenarioLock(): DemoPhoneScenarioLock | null {
  return scenarioLock;
}

/** Returns the unsubscribe function. */
export function subscribeDemoPhoneScenarioLock(listener: () => void): () => void {
  scenarioLockListeners.add(listener);
  return () => {
    scenarioLockListeners.delete(listener);
  };
}

/** Guide to phone: fold the floating phone. */
export const DEMO_PHONE_CLOSE_EVENT = "tarii:demo-phone-close";

/** Guide: fold the demo phone if it is open (no-op when it is folded or absent). */
export function closeDemoPhone(): void {
  window.dispatchEvent(new Event(DEMO_PHONE_CLOSE_EVENT));
}

/** Dock: run `handler` when the guide asks the phone to fold. Returns the unsubscribe function. */
export function subscribeCloseDemoPhone(handler: () => void): () => void {
  window.addEventListener(DEMO_PHONE_CLOSE_EVENT, handler);
  return () => window.removeEventListener(DEMO_PHONE_CLOSE_EVENT, handler);
}

let phoneOpen = false;
const phoneOpenListeners = new Set<() => void>();

/** Dock: report whether the floating phone is on screen. */
export function setDemoPhoneOpen(next: boolean): void {
  if (phoneOpen === next) return;
  phoneOpen = next;
  for (const listener of Array.from(phoneOpenListeners)) listener();
}

/** True while the floating demo phone is open. */
export function isDemoPhoneOpen(): boolean {
  return phoneOpen;
}

/** Returns the unsubscribe function. */
export function subscribeDemoPhoneOpen(listener: () => void): () => void {
  phoneOpenListeners.add(listener);
  return () => {
    phoneOpenListeners.delete(listener);
  };
}

// Installed when this module loads, so a person asked for before the dock
// mounts (the change call moves to the roster page at the same moment) is kept.
if (typeof window !== "undefined") {
  window.addEventListener(DEMO_PHONE_PERSON_EVENT, (e) => {
    const detail = (e as CustomEvent<{ membershipId?: string; open?: boolean }>).detail;
    // A request to show a person without opening the phone is not kept for a later dock.
    if (detail?.membershipId && detail.open !== false) pendingPerson = detail.membershipId;
  });
}

/** The person asked for while no dock listened. Reading clears it. */
export function takePendingPhonePerson(): string | null {
  const id = pendingPerson;
  pendingPerson = null;
  return id;
}

/** Lead page: tell the guide a step is done. */
export function emitGuideAction(detail: string): void {
  window.dispatchEvent(new CustomEvent(DEMO_GUIDE_ACTION_EVENT, { detail }));
}

/** Frame: tell the lead page the member sent the offered request. */
export function notifyParentRequestSent(): void {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: DEMO_PHONE_SENT_MESSAGE }, window.location.origin);
    }
  } catch {
    // No parent page: nothing to tell.
  }
}

/** The Authorization header value for a demo-member token. */
export function demoMemberAuthValue(token: string): string {
  return `Bearer demo-member:${token}`;
}

export type MemberViewSession =
  paths["/api/demo-sessions/current/member-view"]["post"]["responses"]["200"]["content"]["application/json"];

/** Swap the visitor's demo credential for the phone's read-only token. */
export async function mintMemberView(
  demoAuthorization: string,
  membershipId: string,
): Promise<MemberViewSession> {
  return apiFetch<MemberViewSession>("/api/demo-sessions/current/member-view", {
    method: "POST",
    headers: { Authorization: demoAuthorization },
    body: JSON.stringify({ membership_id: membershipId }),
  });
}

/** Lead page: tell the frame the roster changed. No-op where there is no channel. */
export function notifyDemoPhone(): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(DEMO_PHONE_CHANNEL);
    channel.postMessage({ type: "refresh" });
    channel.close();
  } catch {
    // A page without channels keeps the frame as it was.
  }
}

/** Frame: run `onRefresh` whenever the lead page signals. Returns the unsubscribe. */
export function subscribeDemoPhone(onRefresh: () => void): () => void {
  if (typeof BroadcastChannel === "undefined") return () => {};
  const channel = new BroadcastChannel(DEMO_PHONE_CHANNEL);
  channel.onmessage = () => onRefresh();
  return () => channel.close();
}
